/** UNIFORM uses only server-rendered Shopify variants. Never retry a cart mutation. */
export function cartEndpoint(root = '/', action = 'cart.js') {
  if (!['cart.js', 'cart/add.js'].includes(action)) throw new Error('Unsupported cart action');
  if (!/^\/(?!\/)[A-Za-z0-9_/-]*$/.test(root)) throw new Error('Invalid storefront root');
  return `${root.replace(/\/$/, '')}/${action}`;
}

export function selectedItems(pieces) {
  if (!Array.isArray(pieces) || pieces.length > 4) return { error: 'invalid_selection', items: [] };
  const items = [];
  for (const piece of pieces) {
    if (!piece.included) continue;
    // Pre-orders must pass their PDP disclosure/consent flow. Never turn a
    // styling selection into a bare variant add with no preorder properties.
    if (piece.preorder === true || piece.preorder === 'true') return { error: 'preorder_details', items: [] };
    const id = String(piece.variant?.id ?? '');
    if (piece.blocked || !/^\d+$/.test(id) || !Number.isSafeInteger(Number(id)) || Number(id) <= 0
      || !piece.variant.available || piece.variant.disabled) return { error: 'choose_variant', items: [] };
    const minimum = Number(piece.variant.minimum ?? 1);
    const increment = Number(piece.variant.increment ?? 1);
    const maximum = piece.variant.maximum === '' || piece.variant.maximum == null ? null : Number(piece.variant.maximum);
    // This is one of each chosen piece, not a wholesale quantity-rule editor.
    if (minimum !== 1 || increment !== 1 || (maximum !== null && maximum < 1)) return { error: 'choose_variant', items: [] };
    if (items.some((item) => item.id === Number(id))) return { error: 'invalid_selection', items: [] };
    items.push({ id: Number(id), quantity: 1 });
  }
  return items.length ? { error: null, items } : { error: 'choose_pieces', items: [] };
}

export function cartQuantities(cart) {
  if (!cart || !Array.isArray(cart.items)) throw new Error('Invalid cart snapshot');
  const quantities = new Map();
  for (const item of cart.items) {
    const id = String(item.variant_id ?? item.id ?? '');
    const quantity = Number(item.quantity);
    if (!/^\d+$/.test(id) || !Number.isSafeInteger(quantity) || quantity < 0) throw new Error('Invalid cart line');
    quantities.set(id, (quantities.get(id) ?? 0) + quantity);
  }
  return quantities;
}

export function selectedCartIncreased(before, after, items) {
  const previous = cartQuantities(before);
  const current = cartQuantities(after);
  return items.some(({ id }) => (current.get(String(id)) ?? 0) > (previous.get(String(id)) ?? 0));
}

async function requestJson(fetcher, url, options, timeoutMs) {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const response = await fetcher(url, { ...options, signal: controller.signal });
    return { ok: response.ok, body: await response.json() };
  }
  finally { clearTimeout(timeout); }
}

async function readCart(fetcher, root, timeoutMs) {
  const response = await requestJson(fetcher, cartEndpoint(root), { credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'application/json' } }, timeoutMs);
  if (!response.ok) throw new Error('Cart read failed');
  const cart = response.body;
  cartQuantities(cart);
  return cart;
}

/** Injected fetcher and lock keep the mutation path testable without a real cart. */
export function createUniformCart({ fetcher = globalThis.fetch?.bind(globalThis), root = '/', timeoutMs = 15000 } = {}) {
  let busy = false;
  let mutationAttempted = false;
  return {
    get busy() { return busy; },
    get locked() { return mutationAttempted; },
    async add(pieces) {
      if (busy) return { state: 'busy' };
      if (mutationAttempted) return { state: 'review_bag' };
      const selection = selectedItems(pieces);
      if (selection.error) return { state: selection.error };
      busy = true;
      let before;
      try {
        before = await readCart(fetcher, root, timeoutMs);
      } catch {
        busy = false;
        // No mutation was attempted: a deliberate new click is safe here.
        return { state: 'cart_unavailable', attempted: false };
      }
      let succeeded = false;
      try {
        mutationAttempted = true;
        const response = await requestJson(fetcher, cartEndpoint(root, 'cart/add.js'), {
          method: 'POST', credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: selection.items }),
        }, timeoutMs);
        const body = response.body;
        succeeded = response.ok && Array.isArray(body?.items) && !body.status
          && selection.items.every(({ id }) => body.items.some((item) => Number(item.variant_id ?? item.id) === id));
      } catch {
        // A timeout or invalid response does not prove that Shopify rejected the add.
      }
      let after = null;
      try { after = await readCart(fetcher, root, timeoutMs); } catch { /* Show review-bag instead of guessing. */ }
      busy = false;
      if (succeeded) return { state: 'added', cart: after, attempted: true };
      if (!after) return { state: 'uncertain', attempted: true };
      return {
        state: selectedCartIncreased(before, after, selection.items) ? 'partial' : 'not_added',
        cart: after, attempted: true,
      };
    },
  };
}

export function readUniformSelection(panel) {
  return Array.from(panel.querySelectorAll('[data-uniform-piece]')).map((row) => {
    const select = row.querySelector('[data-uniform-variant]');
    const selected = select?.selectedOptions?.[0];
    return {
      included: Boolean(row.querySelector('[data-uniform-include]')?.checked),
      preorder: row.dataset.uniformPreorder === 'true',
      blocked: row.dataset.uniformBlocked === 'true',
      variant: selected ? {
        id: selected.value, available: selected.dataset.available === 'true', disabled: selected.disabled,
        minimum: selected.dataset.minimum, increment: selected.dataset.increment, maximum: selected.dataset.maximum,
      } : null,
    };
  });
}

const clients = new Map();
const mountedPanels = new Set();

export function mountUniformShop(scope = document) {
  const panels = Array.from(scope.querySelectorAll('[data-uniform-shop]'));
  for (const panel of panels) {
    if (panel.dataset.catalogueMode === 'true' || panel.dataset.uniformBound === 'true') continue;
    const form = panel.querySelector('[data-uniform-form]');
    const button = panel.querySelector('[data-uniform-add]');
    const status = panel.querySelector('[data-uniform-status]');
    const review = panel.querySelector('[data-uniform-review]');
    const stringsNode = panel.querySelector('[data-uniform-strings]');
    if (!form || !button || !status || !review || !stringsNode || typeof globalThis.fetch !== 'function') continue;
    let strings;
    try { strings = JSON.parse(stringsNode.textContent); } catch { continue; }
    const root = panel.dataset.root || '/';
    try { cartEndpoint(root); } catch { continue; }
    if (!clients.has(root)) clients.set(root, createUniformCart({ root }));
    const client = clients.get(root);
    panel.dataset.uniformBound = 'true';
    const sync = () => {
      for (const row of panel.querySelectorAll('[data-uniform-piece]')) {
        const include = row.querySelector('[data-uniform-include]');
        const select = row.querySelector('[data-uniform-variant]');
        if (!include) continue;
        include.disabled = row.dataset.uniformBlocked === 'true' || row.dataset.uniformPreorder === 'true' || client.busy;
        if (!select) continue;
        select.disabled = !include.checked || include.disabled;
        select.required = include.checked && !include.disabled;
        const choice = select.selectedOptions?.[0];
        const selected = Boolean(choice?.value) && !choice.disabled;
        row.querySelector('[data-uniform-price]').textContent = selected ? choice.dataset.price : strings.price_prompt;
        row.querySelector('[data-uniform-availability]').textContent = selected ? strings.available : strings.choose_variant;
      }
      const chosen = selectedItems(readUniformSelection(panel));
      button.disabled = client.busy || client.locked || Boolean(chosen.error);
      button.textContent = client.busy ? strings.adding : client.locked ? strings.review_bag : strings.add;
      form.setAttribute('aria-busy', String(client.busy));
      review.hidden = !client.locked;
    };
    const mounted = { panel, sync };
    mountedPanels.add(mounted);
    const syncAll = () => {
      for (const item of mountedPanels) {
        if (!item.panel.isConnected) mountedPanels.delete(item);
        else item.sync();
      }
    };

    form.addEventListener('change', () => {
      sync();
      if (!client.locked && !client.busy) {
        status.textContent = '';
      }
    });
    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      const selection = readUniformSelection(panel);
      const pending = client.add(selection);
      syncAll();
      const result = await pending;
      syncAll();
      if (result.state === 'busy') return;
      status.textContent = strings[result.state] || strings.uncertain;
      status.focus();
      if (result.cart && Number.isSafeInteger(result.cart.item_count) && result.cart.item_count >= 0) {
        document.querySelectorAll('.bag-count').forEach((count) => { count.textContent = `(${result.cart.item_count})`; });
        document.dispatchEvent(new CustomEvent('heir:cart-updated', { detail: { cart: result.cart } }));
      }
    });
    sync();
  }
}

if (typeof document !== 'undefined') {
  mountUniformShop(document);
  document.addEventListener('shopify:section:load', (event) => mountUniformShop(event.target));
}
